import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  commitRosterImport,
  previewRosterImport,
  type ApiError,
} from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type {
  RosterImportCommitResult,
  RosterImportPreviewResult,
} from "@/lib/types";

/**
 * CSV roster import hook (M6b).
 *
 * Owns the two-step preview → commit flow:
 *   - `preview(csv)` dry-runs the import; writes nothing. Stores
 *     the result AND the exact CSV text it ran against.
 *   - `commit(csv)` writes the import (transactional server-side).
 *
 * `previewIsStale(csv)` tells the modal whether the CSV in the
 * textarea still matches what the last preview was run against —
 * the legacy modal re-previews before committing when the text
 * changed, so a coach can't commit a plan they never saw. On a
 * successful commit, `invalidateScope()` refetches the roster +
 * KPIs so the new players appear.
 *
 * Both endpoints are team-admin-only server-side (a plain coach
 * 403s) — the Import CSV button is also gated by role, so the
 * 403 is a backstop.
 */

export type RosterImport = {
  preview: (csvText: string) => Promise<RosterImportPreviewResult>;
  commit: (csvText: string) => Promise<RosterImportCommitResult>;
  /** The last preview result, or `null` before the first preview. */
  previewResult: RosterImportPreviewResult | null;
  /** The last commit result, or `null` before a commit. */
  commitResult: RosterImportCommitResult | null;
  /** True when `csvText` differs from what the last preview ran
   * against (or no preview has run yet) — the modal must re-preview
   * before it allows a commit. */
  previewIsStale: (csvText: string) => boolean;
  /** True when the last preview returned zero error rows — a
   * precondition for enabling commit. */
  previewHasNoErrors: boolean;
  isPreviewing: boolean;
  isCommitting: boolean;
  previewError: ApiError | null;
  commitError: ApiError | null;
  /** Reset all state — call when the modal closes. */
  reset: () => void;
};

export function useRosterImport(): RosterImport {
  // The CSV text the last preview was run against — `null` until
  // the first preview. Drives `previewIsStale`.
  const [previewedCsv, setPreviewedCsv] = useState<string | null>(null);
  const [previewResult, setPreviewResult] =
    useState<RosterImportPreviewResult | null>(null);
  const [commitResult, setCommitResult] =
    useState<RosterImportCommitResult | null>(null);

  const previewMutation = useMutation({
    mutationFn: previewRosterImport,
  });
  const commitMutation = useMutation({
    mutationFn: commitRosterImport,
    onSuccess: () => invalidateScope(),
  });

  const preview = async (csvText: string) => {
    const result = await previewMutation.mutateAsync(csvText);
    setPreviewResult(result);
    setPreviewedCsv(csvText);
    // A fresh preview supersedes any earlier commit result.
    setCommitResult(null);
    return result;
  };

  const commit = async (csvText: string) => {
    const result = await commitMutation.mutateAsync(csvText);
    setCommitResult(result);
    return result;
  };

  const previewIsStale = (csvText: string) =>
    previewedCsv === null || previewedCsv !== csvText;

  const previewHasNoErrors =
    previewResult !== null && previewResult.summary.errors === 0;

  const reset = () => {
    setPreviewedCsv(null);
    setPreviewResult(null);
    setCommitResult(null);
    previewMutation.reset();
    commitMutation.reset();
  };

  return {
    preview,
    commit,
    previewResult,
    commitResult,
    previewIsStale,
    previewHasNoErrors,
    isPreviewing: previewMutation.isPending,
    isCommitting: commitMutation.isPending,
    previewError: (previewMutation.error as ApiError | null) ?? null,
    commitError: (commitMutation.error as ApiError | null) ?? null,
    reset,
  };
}
